/**
 * `/control/me`: who the caller is, what they belong to, and what the server
 * decided they may do.
 *
 * Nothing in the client decides authorisation (platform design §3). This module
 * reads no role out of a token and infers no entitlement from a membership --
 * `capabilities` is a list the API derived, and the shell renders from it. A
 * hidden menu item is a courtesy; the control is the API refusing the call.
 *
 * A membership-less user is a valid, expected state and not an error: a fresh
 * self-serve sign-up is authenticated, holds no membership and is entitled to
 * nothing until an organisation includes it. `/control/me` answers 200 with
 * both arrays empty, and the shell renders that as an empty campaign switcher
 * rather than as a failure.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../lib/http/errors';
import { useAuthState } from './authState';
import { useApiClient } from './client';
import { routePath } from '../../lib/http/routePath';
import { SESSION_GET_ROUTES } from './getRoutes';

import type { Me } from './schemas';

export * from './schemas';

export const ME_PATH = SESSION_GET_ROUTES.me.path;

/** Excluded from the 403 refresh in `./providers`, which would otherwise
 *  chase this query's own refusal forever. */
export const ME_QUERY_KEY = ['control', 'me'] as const;

/**
 * Gated on the session having resolved, and not retried.
 *
 * **`enabled` is the load-bearing line, and names the two states it fires
 * in rather than negating the one it excludes** (`'signed-in'` and
 * `'signed-out'`, never `!== 'loading'`): a negation admits every state
 * added to `SessionState` later, silently. Firebase restores a persisted
 * session asynchronously: for the first few hundred milliseconds after a
 * reload, `onAuthStateChanged` has not answered and `firebaseIdToken()`
 * correctly says there is no token. Asking `/control/me` in that window gets
 * a local `unauthenticated` refusal -- and since this query does not retry,
 * does not refetch on focus, and nothing remounts, the shell then shows a
 * returning user a sign-in wall with no way back. Asking nothing until the
 * session has resolved is the fix; `IdentityReset` in `./providers` is the
 * other half, for when it resolves to a *different* identity than the one
 * already answered for.
 *
 * `retry: false` is deliberate for a different reason. Every other query in
 * this application may retry; this one decides what the caller can see, so a
 * failure has to surface as a refusal now rather than being spun on for three
 * rounds behind a loading state that looks like a hang.
 */
export function useMe(): UseQueryResult<Me, ApiFailureError> {
  const client = useApiClient();
  const session = useAuthState();

  return useQuery<Me, ApiFailureError>({
    queryKey: ME_QUERY_KEY,
    queryFn: () =>
      client.getParsed(routePath(SESSION_GET_ROUTES.me, {}), SESSION_GET_ROUTES.me.schema),
    enabled: session.status === 'signed-in' || session.status === 'signed-out',
    retry: false,
  });
}
